import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/* 路由路径 → 页面 key（保留与原 NAV_LINKS / Footer / 各页面里写的 path 完全一致的字符串，
   这样所有调用点几乎不用改 path 字面量，只需把 <Link to="/x"> 替换为 navigate('/x')）。 */
export const PAGES = {
    HOME: '/',
    RESEARCH: '/research',
    RESEARCH_LLM: '/research/llm',
    RESEARCH_MEDICAL: '/research/medical',
    RESEARCH_LIFE: '/research/life-science',
    RESEARCH_MATH: '/research/math',
    RESEARCH_POWER: '/research/power',
    UPDATES: '/updates',
    ARTICLE_DETAIL: '/article',
    TEAM: '/team',
    ARCK: '/arck',
    NOT_FOUND: '__not_found__',
};

const KNOWN_PAGES = new Set(Object.values(PAGES));

const PageContext = createContext({
    currentPage: PAGES.HOME,
    navigate: () => {},
    isActive: () => false,
});

export function PageProvider({ children }) {
    const [currentPage, setCurrentPage] = useState(() => {
        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        let path = window.location.pathname;
        if (base && path.startsWith(base)) {
            path = path.slice(base.length) || '/';
        }
        path = path.replace(/\/$/, '') || '/';
        return KNOWN_PAGES.has(path) ? path : PAGES.HOME;
    });
    const [articleId, setArticleId] = useState(null);

    const navigate = useCallback((target, opts) => {
        const next = KNOWN_PAGES.has(target) ? target : PAGES.NOT_FOUND;
        if (next === PAGES.ARTICLE_DETAIL && opts?.articleId) {
            setArticleId(opts.articleId);
        }
        setCurrentPage((prev) => {
            if (prev !== next) {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
            return next;
        });
    }, []);

    /* 与原 isPathActive 同义：精确匹配或父路径前缀匹配。
       例如 currentPage = '/research/llm' 时，'/research' 也算 active。 */
    const isActive = useCallback(
        (target) => {
            if (target === PAGES.HOME) return currentPage === PAGES.HOME;
            return currentPage === target || currentPage.startsWith(target + '/');
        },
        [currentPage],
    );

    const value = useMemo(
        () => ({ currentPage, articleId, navigate, isActive }),
        [currentPage, articleId, navigate, isActive],
    );

    return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}

export function usePage() {
    return useContext(PageContext);
}
